import { useTheme } from '@emotion/react';
import { getThemeColors, PaletteName } from './ThemeColors';
/**
 * An emotion styling function that returns a value based on the current palette name.
 * All values are optional, but you should provide a value for each flow-web theme in
 * `PaletteName` to avoid falling back to an unexpected color value.
 *
 * @example
 * const myBackgroundColor = fromThemePalette({ citrixbrand: '#003D48', neutral: '#0045DB' });
 * const StyledComponent = styled.div`
 *   background-color: ${myBackgroundColor};
 * `;
 */
export function fromThemePalette(values) {
  return function (props) {
    var themeWithDefaults = fromEmotionTheme(props.theme);
    return values[themeWithDefaults.paletteName] || values[Object.keys(values)[0]] || '#B1B1B1';
  };
}
/*
 * React Hook that returns a value based on the current palette name.
 * All values are optional, but you should provide a value for each flow-web theme in
 * `PaletteName` to avoid falling back to an unexpected color value.
 *
 * @example
 * const backgroundColor = useThemePalette({ citrixbrand: '#003D48', neutral: '#0045DB' });
 * <Text color={backgroundColor}>Hello World</Text>
 */

export function useThemePalette(values) {
  return fromThemePalette(values)({
    theme: useTheme()
  });
}

/**
 * An emotion styling function that returns the specified theme color.
 *
 * @example
 * const StyledComponent = styled.div`
 *   background-color: ${fromThemeColors('primary')};
 * `;
 */
export function fromThemeColors(color) {
  return function (props) {
    var themeWithDefaults = fromEmotionTheme(props.theme);
    return themeWithDefaults.themeColors[color];
  };
}

/**
 * Provides type-safe read access to the current flow-web theme as
 * provided by emotion.
 *
 * @example
 * export const MyThemedComponent = styled.div`
 * 	background-color: ${props => fromEmotionTheme(props.theme).themeColors.inputBackground};
 * `;
 */
export function fromEmotionTheme(emotionTheme) {
  return emotionTheme.flowweb || createTheme();
}

/**
 * Provides type-safe write access for the current flow web theme as
 * managed by emotion. This is expected to be needed mostly in unit tests.
 *
 * @example
 * import { ThemeProvider } from '@emotion/react';
 * import { Theme, toEmotionTheme } from '@sharefiledev/sharefile-ui-component-library';
 *
 * export function wrapWithFlowWebTheme(element: React.ReactElement, flowWebTheme: Theme) {
 * 	return <ThemeProvider theme={toEmotionTheme(flowWebTheme)}>{element}</ThemeProvider>;
 * }
 */
export function toEmotionTheme(theme) {
  return {
    citrixui: theme,
    flowweb: theme
  };
}
/**
 * React hook that returns the specified theme color.
 */
export function useThemeColor(color) {
  return fromThemeColors(color)({
    theme: useTheme()
  });
}
export function createTheme(themeConfig) {
  var existingTheme = getThemeFromWindow();
  if (!themeConfig && existingTheme) {
    return existingTheme;
  }
  var paletteName = (themeConfig === null || themeConfig === void 0 ? void 0 : themeConfig.paletteName) || PaletteName.neutral;
  var themeColors = getThemeColors(paletteName, themeConfig === null || themeConfig === void 0 ? void 0 : themeConfig.themeColorOverrides);
  var themeWithDefaults = {
    paletteName: paletteName,
    themeColors: themeColors
  };
  return themeWithDefaults;
}
function getThemeFromWindow() {
  var _flowweb_internal__, _flowweb_internal__$c;
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (_flowweb_internal__ = window.__flowweb_internal__) === null || _flowweb_internal__ === void 0 ? void 0 : (_flowweb_internal__$c = _flowweb_internal__.configuration) === null || _flowweb_internal__$c === void 0 ? void 0 : _flowweb_internal__$c.theme;
}